import React, { useState, useEffect } from "react";
import Constants from "../components/Constant";
import {
    TextField,
    Button,
    Box,
    Container,
    Typography,
    Autocomplete,
    Snackbar,
    Alert,
    Grid,
} from "@mui/material";
import MainLayout from "../components/MainLayout";
import backgroundImage from "../assets/backgroundHome.png";

export default function RelatorioPrefeitura() {
    const [prefeituras, setPrefeituras] = useState([]);
    const [filteredPrefeituras, setFilteredPrefeituras] = useState([]);
    const [selectedPrefeitura, setSelectedPrefeitura] = useState(null);
    const [dataInicio, setDataInicio] = useState("");
    const [dataFim, setDataFim] = useState("");
    const [searchPrefeituras, setSearchPrefeituras] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [orgaos, setOrgaos] = useState([]);
    const [selectedOrgao, setSelectedOrgao] = useState(null);
    const [tipoValor, setTipoValor] = useState("BRUTO");
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const showSnackbar = (message, severity = "error") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    // Buscar órgãos
    useEffect(() => {
        const fetchOrgaos = async () => {
            if (!selectedPrefeitura?.PRE_ID) {
                setOrgaos([]);
                setSelectedOrgao(null);
                return;
            }

            try {
                const response = await fetch(`${Constants.API_BASE_URL}/api/orgaosPrefeitura/${selectedPrefeitura.PRE_ID}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "*/*",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    let orgaosArray = [];
                    if (data.orgaos) {
                        orgaosArray = Object.values(data.orgaos);
                    } else if (Array.isArray(data)) {
                        orgaosArray = data;
                    } else if (data.data && Array.isArray(data.data)) {
                        orgaosArray = data.data;
                    }
                    setOrgaos(orgaosArray);
                    setSelectedOrgao(null); // Limpa o órgão selecionado quando mudar a prefeitura
                } else {
                    showSnackbar("Erro ao buscar órgãos da prefeitura.");
                }
            } catch (error) {
                console.error("Erro ao buscar órgãos:", error);
                showSnackbar("Erro ao buscar órgãos da prefeitura.");
            }
        };

        fetchOrgaos();
    }, [selectedPrefeitura, token]);

    useEffect(() => {
        const fetchPrefeituras = async () => {
            try {
                const response = await fetch(Constants.API_CONSULTAR_PREFEITURA, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}` 
                    }
                });
                const data = await response.json();
                if (response.ok && data.prefeituras) {
                    setPrefeituras(data.prefeituras);
                    setFilteredPrefeituras(data.prefeituras);
                } else {
                    showSnackbar("Erro ao buscar prefeituras");
                }
            } catch (error) {
                console.error("Erro ao conectar com o servidor:", error);
                showSnackbar("Erro ao conectar com o servidor.");
            }
        };
        fetchPrefeituras();
    }, [token]);

    useEffect(() => {
        if (searchPrefeituras) {
            const filtered = prefeituras.filter((prefeitura) =>
                prefeitura.PRE_NOME && prefeitura.PRE_NOME.toLowerCase().includes(searchPrefeituras.toLowerCase())
            );
            setFilteredPrefeituras(filtered);
        } else {
            setFilteredPrefeituras(prefeituras);
        }
    }, [searchPrefeituras, prefeituras]);

    const handleGenerateReport = async () => {
        if (!selectedPrefeitura || !dataInicio || !dataFim) {
            showSnackbar("Prefeitura e datas são obrigatórios.");
            return;
        }

        if (!tipoValor) {
            showSnackbar("O tipo de valor é obrigatório.");
            return;
        }

        // Converte o tipo de valor para o formato esperado pela API
        const tipoRelatorio = tipoValor === "LÍQUIDO" ? "liquido" : "bruto";

        try {
            let reportUrl = `${Constants.API_BASE_URL}/api/relatorioDetalhado?PRE_ID=${selectedPrefeitura.PRE_ID}&data_inicio=${dataInicio}&data_fim=${dataFim}&tipo=stream&tipo_relatorio=${tipoRelatorio}`;

            // Adiciona filtro por órgão se selecionado
            if (selectedOrgao) {
                reportUrl += `&ORG_ID=${selectedOrgao.ORG_ID}`;
            }

            const response = await fetch(reportUrl, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/pdf"
                }
            });

            if (response.ok) {
                const blob = await response.blob();
                const fileURL = URL.createObjectURL(blob);
                window.open(fileURL, "_blank");
            } else {
                showSnackbar("Erro ao gerar o relatório.");
            }
        } catch (error) {
            showSnackbar("Erro ao conectar com o servidor.");
        }
    };

    return (
        <MainLayout>
            <Box
                sx={{
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundColor: "rgba(255, 255, 255, 0.8)",
                    backgroundBlendMode: "overlay",
                    padding: 3,
                    borderRadius: 2,
                    boxShadow: 3,
                }}
            >
                <Container maxWidth="md">
                    <Box sx={{ p: 4, bgcolor: "background.paper", borderRadius: 2, boxShadow: 3 }}>
                        <Typography variant="h4" component="h1" gutterBottom align="center">
                            Relatório de Prefeitura
                        </Typography>
                        <Autocomplete
                            options={filteredPrefeituras}
                            getOptionLabel={(option) => option.PRE_NOME || ""}
                            value={selectedPrefeitura}
                            onChange={(_, newValue) => setSelectedPrefeitura(newValue)}
                            onInputChange={(_, newInputValue) => setSearchPrefeituras(newInputValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Prefeitura"
                                    fullWidth
                                    margin="normal"
                                />
                            )}
                        />
                        
                        {/* Campo de Órgão (Opcional) */}
                        <Autocomplete
                            options={orgaos}
                            getOptionLabel={(option) => 
                                option ? `${option.ORG_SIGLA} - ${option.ORG_DESCRICAO}` : ""
                            }
                            value={selectedOrgao}
                            onChange={(_, newValue) => setSelectedOrgao(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Órgão (Opcional)"
                                    fullWidth
                                    margin="normal"
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <strong>{option.ORG_SIGLA}</strong> - {option.ORG_DESCRICAO}
                                </li>
                            )}
                            filterOptions={(options, { inputValue }) => {
                                const filterValue = inputValue.toLowerCase();
                                return options.filter(
                                    option => 
                                        option.ORG_SIGLA.toLowerCase().includes(filterValue) ||
                                        option.ORG_DESCRICAO.toLowerCase().includes(filterValue)
                                );
                            }}
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Data de Início"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={dataInicio}
                                    onChange={(e) => setDataInicio(e.target.value)}
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Data de Fim"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={dataFim}
                                    onChange={(e) => setDataFim(e.target.value)}
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Autocomplete
                                    options={["BRUTO", "LÍQUIDO"]}
                                    value={tipoValor}
                                    onChange={(_, newValue) => setTipoValor(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Tipo de Valor *"
                                            fullWidth
                                            margin="normal"
                                            required
                                            error={!tipoValor}
                                            helperText={!tipoValor ? "Tipo de valor é obrigatório" : ""}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                            <Button variant="contained" color="primary" onClick={handleGenerateReport}>
                                Gerar Relatório
                            </Button>
                        </Box>
                    </Box>
                </Container>
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Box>
        </MainLayout>
    );
}